import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2, Sparkles } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { AIBlogGeneratorModal } from '@/components/admin/AIBlogGeneratorModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function BlogDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showAIModal, setShowAIModal] = useState(false);

  const { data: posts, isLoading, error } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, published, created_at, category')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Mutation: Neuen Post erstellen
  const createPostMutation = useMutation({
    mutationFn: async () => {
      const timestamp = Date.now();
      const defaultTitle = `Neuer Post ${new Date().toLocaleDateString('de-DE')}`;
      
      // Slug-Generator-Funktion
      const generateSlug = (title: string) => {
        return title
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '') // Entferne Umlaute
          .replace(/ä/g, 'ae')
          .replace(/ö/g, 'oe')
          .replace(/ü/g, 'ue')
          .replace(/ß/g, 'ss')
          .replace(/[^a-z0-9]+/g, '-') // Alles außer Buchstaben/Zahlen → Bindestrich
          .replace(/^-+|-+$/g, ''); // Entferne Start/End-Bindestriche
      };
      
      const slug = generateSlug(defaultTitle) + `-${timestamp}`;
      
      const { data, error } = await supabase
        .from('blog_posts')
        .insert({
          title: defaultTitle,
          slug: slug,
          published: false,
          content: '<p>Beginne hier mit dem Schreiben...</p>'
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: '✅ Post erstellt!',
        description: 'Leerer Post wurde angelegt.'
      });
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      navigate(`/admin/blog/edit/${data.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: '❌ Fehler beim Erstellen',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Mutation: Post löschen
  const deletePostMutation = useMutation({
    mutationFn: async (postId: string) => {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', postId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: '✅ Post gelöscht!',
        description: 'Der Post wurde erfolgreich gelöscht.'
      });
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
    },
    onError: (error: Error) => {
      toast({
        title: '❌ Fehler beim Löschen',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Lädt Blog-Posts...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">
              ❌ Fehler beim Laden: {error.message}
            </p>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AIBlogGeneratorModal open={showAIModal} onOpenChange={setShowAIModal} />
      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <CardTitle className="text-xl sm:text-2xl">Blog-Posts ({posts?.length || 0})</CardTitle>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button 
              onClick={() => setShowAIModal(true)}
              variant="default"
              className="gap-2 w-full sm:w-auto"
            >
              <Sparkles className="h-4 w-4" />
              <span className="hidden xs:inline">KI-Artikel generieren</span>
              <span className="xs:hidden">KI-Artikel</span>
            </Button>
            <Button 
              onClick={() => createPostMutation.mutate()}
              disabled={createPostMutation.isPending}
              variant="outline"
              className="gap-2 w-full sm:w-auto"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden xs:inline">{createPostMutation.isPending ? 'Erstellt...' : 'Leerer Post'}</span>
              <span className="xs:hidden">{createPostMutation.isPending ? '...' : 'Neu'}</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {posts && posts.length > 0 ? (
            <div className="space-y-3">
              {posts.map((post) => (
                <div 
                  key={post.id} 
                  className="flex justify-between items-center p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{post.title}</h3>
                    <div className="flex gap-4 mt-1">
                      <span className="text-sm text-muted-foreground">
                        {post.published ? '✅ Veröffentlicht' : '📝 Entwurf'}
                      </span>
                      {post.category && (
                        <>
                          <span className="text-muted-foreground/50">•</span>
                          <span className="text-sm text-primary">
                            📁 {post.category}
                          </span>
                        </>
                      )}
                      <span className="text-muted-foreground/50">•</span>
                      <span className="text-sm text-muted-foreground">
                        🕒 {formatDistanceToNow(new Date(post.created_at), { 
                          addSuffix: true, 
                          locale: de 
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/blog/${post.slug}`)}
                    >
                      👁️ Vorschau
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/admin/blog/edit/${post.id}`)}
                    >
                      ✏️ Bearbeiten
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Post wirklich löschen?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Diese Aktion kann nicht rückgängig gemacht werden. Der Post "{post.title}" wird dauerhaft gelöscht.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deletePostMutation.mutate(post.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Löschen
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg mb-4">
                Noch keine Blog-Posts vorhanden
              </p>
              <Button 
                onClick={() => createPostMutation.mutate()}
                disabled={createPostMutation.isPending}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Ersten Post erstellen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
